/**
 * BACKOFFICE LOGISTIQUE — Livraison / retour / réexpédition (#662).
 * Méthodes extraites telles quelles du god-service `backoffice.service.ts`
 * (workstream C) : tri livraison, assignation partenaire, confirmation /
 * échec / re-programmation de livraison, cycle retour (#410) et reshipment.
 */

import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, updateDoc, getDoc, limit, orderBy } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { ShipmentListItem } from '../models/backoffice.models';
import { compareByCreatedAtDesc, extractCreatedAt, mapToListItem } from './backoffice-mapper';
import { BackofficeActivityService } from './backoffice-activity.service';

/**
 * #1376 — plafond de chargement de la vue « Livraisons ».
 *
 * ALIGNÉ sur celui de la liste Colis (`getAllShipmentsForList` : 500 documents
 * par tranche de phases). Les deux écrans lisent la même collection, appliquent
 * leur recherche et leurs filtres CÔTÉ CLIENT sur ce qui a été chargé, et n'ont
 * donc aucune raison de voir des parcs de tailles différentes — l'écart 200/500
 * n'était exprimé nulle part.
 *
 * Le plafond reste nécessaire (il évite le scan complet de la collection) ;
 * ce qui change avec #1376, c'est qu'il **se voit** quand il est atteint.
 */
export const DELIVERY_LIST_LIMIT = 500;

/** #2190 — les colis livrés affichés ; les plus anciens se retrouvent par la liste Colis. */
export const DELIVERED_LIST_LIMIT = 200;

/** #1376 — une page de la vue « Livraisons » : la liste ET ce que le plafond en a coupé. */
export interface DeliveryListPage {
  /** Colis retenus par le filtre métier, triés du plus récent au plus ancien. */
  items: ShipmentListItem[];
  /** Documents RAMENÉS par la requête, avant tout filtre client. */
  loadedCount: number;
  /** Plafond appliqué à la requête. */
  limit: number;
  /** `true` dès que la requête a ramené autant de documents que le plafond. */
  truncated: boolean;
}

/**
 * #1376 — mise en forme d'une page de livraison, extraite de
 * `getShipmentsForDelivery` pour être testable sans Firestore.
 *
 * 🔴 La troncature se mesure sur les **documents lus**, jamais sur les items
 * rendus : le filtre métier ci-dessous s'applique APRÈS le plafond. Un écran
 * qui affiche 12 lignes peut très bien avoir buté sur les 500 documents du
 * plafond — compter les items rendrait le plafond invisible, c'est-à-dire
 * exactement le défaut corrigé ici.
 */
export function buildDeliveryListPage(
  docs: ReadonlyArray<{ id: string; data(): any }>,
  limitCount: number,
  partnerId?: string
): DeliveryListPage {
  // Show shipments that have a partner assigned OR have status out_for_delivery/delivered/delivery_sorted
  let items = docs
    .filter(d => {
      const data = d.data();
      const hasPartner = !!data['delivery']?.['partnerId'];
      const status = data['status']?.['current'];
      // Include all delivery-phase shipments (with or without partner)
      // #416 — `awaiting_pickup` explicitement listé : un colis à retirer n'a
      // pas forcément de partenaire assigné, il disparaissait donc de l'écran
      // alors que c'est là qu'on confirme sa remise au destinataire.
      return hasPartner || status === 'out_for_delivery' || status === 'delivered'
        || status === 'delivery_sorted' || status === 'awaiting_pickup';
    })
    .map(d => mapToListItem(d));
  if (partnerId) {
    const partnerShipmentIds = new Set(
      docs.filter(d => d.data()['delivery']?.['partnerId'] === partnerId).map(d => d.id)
    );
    items = items.filter(item => partnerShipmentIds.has(item.id));
  }
  return {
    items: items.sort(compareByCreatedAtDesc),
    loadedCount: docs.length,
    limit: limitCount,
    truncated: docs.length >= limitCount,
  };
}

/**
 * #2190 — réunit les colis livrés lus par forme de date, garde les plus récents.
 * Tronquée dès qu'une des requêtes a buté sur le plafond : les plus anciens manquent.
 */
export function buildDeliveredListPage(
  docsByForm: ReadonlyArray<ReadonlyArray<{ id: string; data(): any }>>,
  limitCount: number,
  partnerId?: string,
): DeliveryListPage {
  const merged = docsByForm
    .flat()
    .map(d => ({ d, at: extractCreatedAt(d.data())?.getTime() ?? 0 }))
    .sort((a, b) => b.at - a.at)
    .slice(0, limitCount)
    .map(entry => entry.d);
  const page = buildDeliveryListPage(merged, limitCount, partnerId);
  return { ...page, truncated: docsByForm.some(docs => docs.length >= limitCount) };
}

@Injectable({ providedIn: 'root' })
export class DeliveryService {

  constructor(
    private firestore: Firestore,
    private functions: Functions,
    private activity: BackofficeActivityService
  ) {}

  // ── Tri livraison ──

  async getShipmentsForSorting(limitCount = 200): Promise<ShipmentListItem[]> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    // Include both standard (congo_distribution) and heavy (heavy_delivery) parcels
    const q = query(shipmentsRef, where('currentPhase', 'in', ['congo_distribution', 'heavy_delivery']), limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs
      .filter(d => {
        const data = d.data();
        // Exclude shipments already assigned to a partner
        if (data['delivery']?.['partnerId']) return false;
        // Exclude blocked parcels from degroupage (CRITICAL GAP 1 fix)
        if (data['congoArrival']?.['parcelStatus'] === 'blocked') return false;
        // Only include shipments that have been through degroupage (scanned) or have no container (direct)
        const hasContainer = !!data['expedition']?.['containerId'];
        if (hasContainer && !data['congoArrival']?.['scannedAt']) return false;
        return true;
      })
      .map(d => mapToListItem(d))
      .sort(compareByCreatedAtDesc);
  }

  async assignDeliveryPartner(shipmentId: string, partnerId: string, partnerName: string): Promise<void> {
    // #399 — CF de transition : assignation partenaire + passage out_for_delivery
    // atomiques et validés serveur (from delivery_sorted/local_sorting/...).
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'out_for_delivery',
      assign: { partnerId, partnerName },
      details: { partnerId, partnerName },
      ...(location && { location }),
    });
  }

  /**
   * #414 — déclare perdu un colis manquant au dégroupage (ou introuvable en
   * distribution). Transition serveur `lost` (réservée manager+, notification
   * client via le dispatcher, timestamps.lostAt).
   */
  async declareShipmentLost(shipmentId: string, containerId?: string, reason?: string): Promise<void> {
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'lost',
      details: {
        ...(containerId && { containerId }),
        reason: reason || 'missing_at_unpacking',
      },
      ...(location && { location }),
    });
  }

  async confirmDeliverySorting(shipmentId: string): Promise<void> {
    // #399 — CF de transition : delivery_sorted validé serveur (from
    // local_sorting/destination_sorted), status.label + statusHistory + phase
    // congo_distribution écrits par la CF (le libellé canonique de la matrice
    // remplace l'ancien « Trié par mode de livraison »).
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'delivery_sorted',
      details: { sortedBy: 'congo_staff' },
      ...(location && { location }),
    });
    // Flags de tri congoArrival.* (pas des champs status) — updateDoc client
    // conservé, APRÈS la CF (pas de flag posé si la transition est refusée).
    const shipmentRef = doc(this.firestore, 'shipments', shipmentId);
    await updateDoc(shipmentRef, {
      'congoArrival.deliverySorted': true,
      'congoArrival.deliverySortedAt': new Date(),
    });
  }

  // ── Livraison ──

  /**
   * #1376 — rend la page complète (liste + état du plafond) et non plus un
   * simple tableau : passé le plafond, la recherche et les filtres de l'écran,
   * qui s'appliquent côté client sur cette liste, cesseraient de voir le parc
   * sans rien dire. L'appelant a désormais de quoi le dire.
   *
   * ⚠️ La requête n'a pas d'`orderBy` : les documents retenus par le plafond
   * sont les premiers dans l'ordre des identifiants, pas les plus récents. Une
   * troncature est donc arbitraire — raison de plus pour l'annoncer.
   */
  async getShipmentsForDelivery(partnerId?: string, limitCount = DELIVERY_LIST_LIMIT): Promise<DeliveryListPage> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    // #2190 — sans les livrés : leur nombre ne fait que croître, ils prendraient la place des colis à livrer.
    const q = query(shipmentsRef, where('currentPhase', 'in', ['congo_distribution', 'heavy_delivery']), limit(limitCount));
    const snap = await getDocs(q);
    return buildDeliveryListPage(snap.docs, limitCount, partnerId);
  }

  /**
   * #2190 — l'onglet « Livrées », sur ses propres requêtes, bornées et triées.
   *
   * `timestamps.createdAt` porte un Timestamp ou une chaîne, et Firestore trie par
   * type avant de trier par valeur : une requête unique rendrait d'abord toutes les
   * chaînes, même anciennes (mesuré en dev et en acc). Une requête par forme, puis
   * un tri commun. Index existant : `currentPhase` + `timestamps.createdAt` DESC.
   */
  async getDeliveredShipments(partnerId?: string, limitCount = DELIVERED_LIST_LIMIT): Promise<DeliveryListPage> {
    const shipmentsRef = collection(this.firestore, 'shipments');
    const byForm = (lower: string | Date) => getDocs(query(
      shipmentsRef,
      where('currentPhase', '==', 'delivered'),
      where('timestamps.createdAt', '>=', lower),
      orderBy('timestamps.createdAt', 'desc'),
      limit(limitCount),
    ));
    const [strings, timestamps] = await Promise.all([byForm(''), byForm(new Date(0))]);
    return buildDeliveredListPage([strings.docs, timestamps.docs], limitCount, partnerId);
  }

  async getShipmentForDelivery(shipmentId: string): Promise<any> {
    const docRef = doc(this.firestore, 'shipments', shipmentId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  async confirmDelivery(
    shipmentId: string,
    trackingNumber: string,
    proof: {
      deliveryPhotoUrl: string;
      recipientIdPhotoUrl: string;
      signatureUrl: string;
      receivedBy: string;
    },
    receiverInfo: { name: string; phone: string; email?: string }
  ): Promise<void> {
    // #399 (PR C) — CF de transition : preuve exigée + persistée ET stats
    // partenaire incrémentées DANS la même transaction. La garde d'idempotence
    // (déjà `delivered` ⇒ no-op) empêche le double-comptage sur double-clic (#413).
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'delivered',
      proof: {
        deliveryPhotoUrl: proof.deliveryPhotoUrl,
        recipientIdPhotoUrl: proof.recipientIdPhotoUrl,
        signatureUrl: proof.signatureUrl,
        receivedBy: proof.receivedBy,
      },
      details: { receivedBy: proof.receivedBy },
      ...(location && { location }),
    });

    // #412 — pas d'envoi frontend : `delivered` est trigger-enabled, la
    // notification client (sender + SMS destinataire) est portée par
    // statusNotificationDispatchTrigger avec son flag d'idempotence
    // notifications.delivered.sent. L'envoi frontend créait un DOUBLON.
  }

  /**
   * #416 — « Prêt au retrait » : le colis attend son destinataire au point relais
   * / à l'entrepôt RDC. Transition serveur `awaiting_pickup` (from local_sorting,
   * delivery_sorted, out_for_delivery, delivery_attempted).
   *
   * Le statut est trigger-enabled : le destinataire est prévenu (SMS compris) par
   * `statusNotificationDispatchTrigger`, pas d'envoi frontend ici — un envoi
   * client ferait un doublon, comme pour `delivered` (#412).
   *
   * @param pickupLocation point de retrait communiqué au destinataire (facultatif)
   */
  async markReadyForPickup(shipmentId: string, pickupLocation?: string, notes?: string): Promise<void> {
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'awaiting_pickup',
      details: {
        ...(pickupLocation ? { pickupLocation } : {}),
        ...(notes ? { notes } : {}),
      },
      ...(location && { location }),
    });
  }

  async recordFailedDelivery(
    shipmentId: string,
    reason: string,
    notes?: string,
    rescheduledTo?: Date,
    senderInfo?: { email?: string; phone?: string; name?: string },
    receiverInfo?: { email?: string; phone?: string; name?: string },
    trackingNumber?: string,
    deliveryMethod?: string
  ): Promise<void> {
    // #399 (PR C-bis) — CF de transition : précondition `from out_for_delivery`,
    // failureReason résolu serveur, append `failedAttempts` et stats partenaire
    // (échec) DANS la transaction. La garde d'idempotence évite le doublon
    // d'entrée / le double-comptage sur double-clic.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'delivery_attempted',
      failure: { reason, notes: notes || '', rescheduledTo: rescheduledTo ? rescheduledTo.toISOString() : undefined },
      details: { reason, ...(notes ? { notes } : {}) },
      ...(location && { location }),
    });

    // #412 — pas d'envoi frontend : `delivery_attempted` est trigger-enabled ;
    // le dispatcher lit `delivery.failureReason` (écrit serveur par la CF) et
    // notifie sender + destinataire avec idempotence. Les 2 envois frontend
    // (sender + receiver) créaient des DOUBLONS.
  }

  // ── Re-livraison / Retour ──
  // (MAX_DELIVERY_ATTEMPTS déplacé côté serveur dans transitionShipmentStatus, #399)

  async rescheduleDelivery(
    shipmentId: string,
    newDate: Date,
    partnerId?: string,
    partnerName?: string,
    notes?: string
  ): Promise<void> {
    // #399 (PR C-ter) — CF de transition : précondition `from delivery_attempted`
    // + règle MAX_DELIVERY_ATTEMPTS évaluées serveur DANS la transaction (fin du
    // TOCTOU read-check-write). La CF pose le libellé dynamique daté + les champs
    // delivery.rescheduledTo/At + partenaire éventuel.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'delivery_rescheduled',
      reschedule: {
        newDate: newDate.toISOString(),
        ...(partnerId && { partnerId }),
        ...(partnerName && { partnerName }),
      },
      details: { ...(notes ? { notes } : {}) },
      ...(location && { location }),
    });

    // #938 — pas d'envoi frontend : `delivery_rescheduled` est trigger-enabled
    // (#805), la notification est portée par statusNotificationDispatchTrigger
    // → client primaire email + SMS, destinataire Congo SMS, avec la nouvelle
    // date lue de `delivery.rescheduledTo` (écrit par la MÊME transaction que
    // `status.current`) et la clé d'idempotence `delivery_rescheduled_<date>`.
    //
    // Le bloc supprimé ici n'était PAS un doublon : il appelait
    // `sendLogisticsNotification` avec `type: 'delivery_rescheduled'`, type
    // absent de `LogisticsNotificationType` depuis toujours → les deux
    // dispatchers tombaient en `default` (`success: false`), la CF renvoyait
    // quand même `success: true` au niveau supérieur, et la timeline recevait
    // un « Notification envoyee (email + sms) » MENSONGER. Zéro message envoyé,
    // une trace fausse : sa suppression n'enlève aucun envoi réel.
  }

  async confirmRedeliveryDispatch(shipmentId: string): Promise<void> {
    // #399 (PR C-ter) — CF de transition : précondition `from delivery_rescheduled`
    // évaluée serveur (fin du TOCTOU). out_for_delivery est trigger-enabled → la
    // notification client est portée par le trigger.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'out_for_delivery',
      details: { redispatch: true },
      ...(location && { location }),
    });
  }

  async markForReturn(shipmentId: string, reason: string): Promise<void> {
    // #399 — CF de transition : ajoute le gate manager+ (returned_to_sender) et
    // l'atomicité. La CF écrit delivery.returnReason (lu par le trigger de notif
    // #385) et pose currentPhase=return_to_sender. Notification portée par le
    // trigger sur le passage à 'returned_to_sender'.
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'returned_to_sender',
      returnReason: reason,
      details: { reason },
      ...(location && { location }),
    });
  }

  // ── Flow aval du retour (#410) ──

  /**
   * Réception physique d'un colis retourné (scan réception d'un colis en
   * returned_to_sender). Transition return_received_at_warehouse : stamps
   * delivery.returnReceivedAt / timestamps.returnReceivedAt côté serveur.
   */
  async markReturnReceived(shipmentId: string): Promise<void> {
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'return_received_at_warehouse',
      details: { registeredBy: 'warehouse_staff' },
      ...(location && { location }),
    });
  }

  /**
   * Facturation du retour (#410) : crée un Stripe Payment Link pour les frais
   * de gestion du retour (pattern weight-surcharge, metadata.type 'return_fee')
   * et l'envoie au client par email + SMS. Le montant est re-validé serveur.
   */
  async createReturnFeePaymentLink(
    shipmentId: string,
    amount: number,
    reason?: string
  ): Promise<{ success: boolean; paymentLinkUrl: string; notifications?: { emailSent: boolean; smsSent: boolean } }> {
    const fn = httpsCallable<any, any>(this.functions, 'createReturnFeePaymentLink');
    const result = await fn({ shipmentId, amount, ...(reason ? { reason } : {}) });
    return result.data;
  }

  /**
   * Clôture du retour : colis remis à l'expéditeur (récupération sur place).
   * Gatée serveur sur le paiement des frais de retour éventuels (waiver via
   * force, manager+). La réexpédition reste une décision produit (#410).
   */
  async completeReturn(shipmentId: string, notes?: string, force = false): Promise<void> {
    const location = await this.activity.captureLocation();
    const fn = httpsCallable<any, any>(this.functions, 'transitionShipmentStatus');
    await fn({
      shipmentId,
      targetStatus: 'return_completed',
      ...(notes ? { notes } : {}),
      // force = waiver des frais de retour impayés — le serveur le réserve
      // aux manager+ et marque returnFee.status='waived' dans la transaction.
      ...(force ? { force: true } : {}),
      ...(location && { location }),
    });
  }

  /**
   * Réexpédition (#410) : crée un nouveau shipment lié vers un nouveau
   * destinataire (le colis physique, déjà à l'entrepôt, reprend le flux
   * nominal) et clôture le dossier de retour d'origine. Réservé manager+.
   */
  async createReshipment(
    shipmentId: string,
    receiver: {
      firstName: string;
      lastName: string;
      email?: string;
      phone?: { prefix: string; number: string; country?: string };
      address: { street?: string; city: string; country?: string };
    },
    reason?: string
  ): Promise<{ success: boolean; reshipmentId: string; trackingNumber: string }> {
    const fn = httpsCallable<any, any>(this.functions, 'createReshipment');
    const res = await fn({ shipmentId, receiver, ...(reason ? { reason } : {}) });
    return res.data;
  }
}
